import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, IconButton, LinearProgress, Paper, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiService, HoldingAlert, PortfolioRisk as Risk } from '../services/api';
import PositionSizer from '../components/PositionSizer';
import TaxCheck from '../components/TaxCheck';

const money = (v?: number | null) => (v == null ? '–' : `$${Math.round(v).toLocaleString()}`);
const pct = (v?: number | null, d = 1) => (v == null ? '–' : `${(v * 100).toFixed(d)}%`);
const signed = (v?: number | null) => (v == null ? '–' : `${v > 0 ? '+' : ''}${(v * 100).toFixed(1)}%`);

function Stat({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h5" fontWeight={700} color={color}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function PortfolioRisk() {
  const [data, setData] = useState<Risk | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sym, setSym] = useState('');
  const [shares, setShares] = useState('');
  const [cost, setCost] = useState('');
  const [sizeFor, setSizeFor] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<HoldingAlert[]>([]);
  const [checking, setChecking] = useState(false);
  useEffect(() => { apiService.getHoldingAlerts().then(setAlerts).catch(() => undefined); }, []);
  const checkNow = () => {
    setChecking(true);
    apiService.checkHoldingAlerts().then(r => { setAlerts(r.alerts); setNotice(`${r.new} new alert(s).`); })
      .catch(e => setError(e?.response?.data?.detail || 'Check failed')).finally(() => setChecking(false));
  };

  const refresh = useCallback(() => {
    setLoading(true);
    apiService.getPortfolioRisk().then(d => { setData(d); setError(null); })
      .catch(e => setError(e?.response?.data?.detail || e?.message || 'Failed to load')).finally(() => setLoading(false));
  }, []);
  useEffect(refresh, [refresh]);

  const add = () => {
    if (!sym.trim() || !(Number(shares) > 0)) return;
    apiService.addHolding(sym.trim().toUpperCase(), Number(shares), Number(cost) > 0 ? Number(cost) : undefined)
      .then(() => { setSym(''); setShares(''); setCost(''); refresh(); })
      .catch(e => setError(e?.response?.data?.detail || 'Could not add'));
  };
  const remove = (s: string) => apiService.removeHolding(s).then(refresh);
  const importFrom = (src: 'journal' | 'ibkr') =>
    apiService.importHoldings(src).then(r => { setNotice(`Imported ${r.imported} position(s) from ${src === 'ibkr' ? 'IBKR' : 'the Trade Journal'}.`); refresh(); })
      .catch(e => setError(e?.response?.data?.detail || 'Import failed'));

  const s = data?.summary;
  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Portfolio Risk</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        What your holdings could lose, how closely they move with the market, and where the money is concentrated.
        The bad-month figures come from the past year's daily moves at today's weights. They were tested on random
        portfolios of 3–20 stocks at 103 month-ends since 2018: a month worse than the "1 in 10" line came 9–10 times in
        100, as it should.
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" label="Symbol" value={sym} onChange={e => setSym(e.target.value)} sx={{ width: 110 }} />
          <TextField size="small" label="Shares" type="number" value={shares} onChange={e => setShares(e.target.value)} sx={{ width: 110 }} />
          <TextField size="small" label="Cost / share (optional)" type="number" value={cost} onChange={e => setCost(e.target.value)} sx={{ width: 170 }} />
          <Button variant="contained" onClick={add}>Add / update</Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="outlined" onClick={() => importFrom('journal')}>Import from Trade Journal</Button>
          <Tooltip title="Needs TWS running and connected">
            <Button variant="outlined" onClick={() => importFrom('ibkr')}>Import from IBKR</Button>
          </Tooltip>
        </Box>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {!loading && data && !s && (
        <Alert severity="info">Add holdings above, or import them, to see the risk figures.</Alert>
      )}

      {s && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}><Stat label="Value" value={money(s.value)} sub={`${s.holdings} holdings · prices of ${s.as_of}`} /></Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stat label="A bad month (1 in 10)" value={s.bad_month ? `−${money(s.bad_month.dollars)}` : '–'} color="error.main"
                sub={s.bad_month ? `−${pct(s.bad_month.pct)}; 1 in 20: −${money(s.very_bad_month?.dollars)} (−${pct(s.very_bad_month?.pct)})` : 'needs a year of prices'} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stat label="A bad quarter (1 in 10)" value={s.bad_quarter ? `−${money(s.bad_quarter.dollars)}` : '–'} color="error.main"
                sub={s.bad_quarter ? `−${pct(s.bad_quarter.pct)} over 3 months` : undefined} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stat label="Moves with the market" value={s.beta != null ? `beta ${s.beta.toFixed(2)}` : '–'}
                sub={s.correlation != null ? `correlation ${s.correlation.toFixed(2)} · swings ${pct(s.annual_vol, 0)} a year vs SPY ${pct(s.spy_annual_vol, 0)}` : undefined} />
            </Grid>
          </Grid>
          {s.share_with_a_year_of_prices < 0.95 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Only {pct(s.share_with_a_year_of_prices, 0)} of the money is in stocks with a year of prices; the figures
              cover that part.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2, overflowX: 'auto' }}>
                <Typography variant="subtitle1" fontWeight={600}>Holdings</Typography>
                <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', px: 1 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Stock</TableCell><TableCell align="right">Value</TableCell>
                      <TableCell align="right">Weight</TableCell>
                      <TableCell align="right"><Tooltip title="Its share of the portfolio's ups and downs"><span>Share of the risk</span></Tooltip></TableCell>
                      <TableCell align="right"><Tooltip title="Its own 1-in-10 bad month, from its past year"><span>Bad-month price</span></Tooltip></TableCell>
                      <TableCell align="right">Gain</TableCell><TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data!.holdings.map(h => (
                      <TableRow key={h.symbol} hover selected={sizeFor === h.symbol}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{h.symbol}{h.stale && <Chip size="small" label="old price" sx={{ ml: 1, height: 16 }} />}</Typography>
                          <Typography variant="caption" color="text.secondary">{h.shares} sh · {h.sector}</Typography>
                        </TableCell>
                        <TableCell align="right">{money(h.value)}</TableCell>
                        <TableCell align="right">{pct(h.weight, 0)}</TableCell>
                        <TableCell align="right" sx={{ color: h.risk_share != null && h.risk_share > h.weight * 1.8 ? 'error.main' : undefined, fontWeight: 600 }}>
                          {pct(h.risk_share, 0)}
                        </TableCell>
                        <TableCell align="right">
                          {h.bad_month_price != null ? <>${h.bad_month_price.toFixed(2)} <Typography component="span" variant="caption" color="text.secondary">(−{money(h.bad_month_dollars)})</Typography></> : '–'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: (h.gain_pct ?? 0) >= 0 ? 'success.main' : 'error.main' }}>
                          {h.gain_pct != null ? `${h.gain_pct > 0 ? '+' : ''}${h.gain_pct.toFixed(1)}%` : '–'}
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => setSizeFor(sizeFor === h.symbol ? null : h.symbol)}>size</Button>
                          <IconButton size="small" onClick={() => remove(h.symbol)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {sizeFor && (() => {
                  const h = data!.holdings.find(x => x.symbol === sizeFor);
                  return h && h.price && h.bad_month_price ? (
                    <PositionSizer price={h.price} low={h.bad_month_price} lowLabel={`its 1-in-10 bad-month price`} />
                  ) : null;
                })()}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>Concentration</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Largest holding {pct(s.largest_weight, 0)}, top three {pct(s.top3_weight, 0)}. Acts like{' '}
                  <strong>{s.effective_holdings}</strong> equal holdings.
                </Typography>
                {Object.entries(s.sectors).map(([sec, w]) => (
                  <Box key={sec} sx={{ mb: 0.75 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption">{sec}</Typography><Typography variant="caption">{pct(w, 0)}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={Math.min(100, w * 100)} />
                  </Box>
                ))}
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>Through past market falls</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Today's weights, held through each fall (stocks that were trading then).
                </Typography>
                <Table size="small">
                  <TableBody>
                    {s.falls.map(f => (
                      <TableRow key={f.label}>
                        <TableCell sx={{ px: 0.5 }}>{f.label}</TableCell>
                        <TableCell align="right" sx={{ px: 0.5, color: 'error.main', fontWeight: 600 }}>{signed(f.portfolio_pct)}</TableCell>
                        <TableCell align="right" sx={{ px: 0.5 }}><Typography variant="caption" color="text.secondary">SPY {signed(f.spy_pct)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
          <TaxCheck version={data} />
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" fontWeight={600}>Alerts on these holdings</Typography>
              <Button size="small" onClick={checkNow} disabled={checking}>{checking ? 'Checking…' : 'Check now'}</Button>
              <Typography variant="caption" color="text.secondary">
                Checked every weekday after the close and sent to Telegram: a week outside the tested range, an exit
                signal SELL, a notable 8-K filing.
              </Typography>
            </Box>
            {alerts.length === 0
              ? <Typography variant="body2" color="text.secondary">No alerts in the last 30 days.</Typography>
              : alerts.map((a, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                    <Chip size="small" label={({ unusual_week: 'unusual week', exit_sell: 'exit SELL', '8k': '8-K' } as Record<string, string>)[a.kind] ?? a.kind}
                      sx={{ mr: 1, height: 18 }} color={a.kind === 'exit_sell' ? 'error' : 'default'} />
                    <Typography component="span" variant="caption" color="text.secondary">{a.created_at.slice(0, 10)} </Typography>
                    {a.message}
                  </Typography>
                ))}
          </Paper>
          <Typography variant="caption" color="text.secondary">
            A bad month is one the past year says happens about 1 time in 10; a worse one is possible, and 1 in 20 is
            worse again. Past falls replay today's weights and miss stocks that were not trading then.
          </Typography>
        </>
      )}
      {loading && !data && <CircularProgress />}
    </Box>
  );
}
